import React from 'react';
import InputMask from 'react-input-mask';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {VideoEditorText} from '../VideoEditorText';
import {
  Slot,
  SlotBadge,
  SlotStatus,
  isSlotLocked,
  slotBadgeOf,
  slotDurationOf,
} from './editMediaTypes';
import {useEditMediaState} from './useEditMediaState';
import {ReactComponent as DeleteIcon} from '../../../assets/images/trash-icon.svg';
import {ReactComponent as LockIcon} from '../../../assets/images/slot-lock.svg';
import {ReactComponent as VideoTypeIcon} from '../../../assets/images/artifact-type-video.svg';
import {ReactComponent as ImageTypeIcon} from '../../../assets/images/artifact-type-image.svg';
import styles from './EditMediaSlotsPanel.module.css';

const formatDuration = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(m)}:${pad(sec)}`;
};

const parseDuration = (text: string): number | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d{1,3}):(\d{1,2})$/);
  if (match) {
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    if (Number.isNaN(minutes) || Number.isNaN(seconds) || seconds >= 60)
      return null;
    return minutes * 60 + seconds;
  }
  const num = Number(trimmed);
  if (!Number.isNaN(num) && num >= 0) return Math.round(num);
  return null;
};

const durationMask = [/[0-9]/, /[0-9]/, ':', /[0-5]/, /[0-9]/];

interface TimeInputProps {
  value: number;
  disabled: boolean;
  min?: number;
  max?: number;
  onCommit: (seconds: number) => void;
}

const TimeInput = ({value, disabled, min, max, onCommit}: TimeInputProps) => {
  const [text, setText] = React.useState(formatDuration(value));

  React.useEffect(() => {
    setText(formatDuration(value));
  }, [value]);

  const commit = () => {
    const parsed = parseDuration(text);
    if (parsed === null) {
      setText(formatDuration(value));
      return;
    }
    let clamped = parsed;
    if (min !== undefined) clamped = Math.max(min, clamped);
    if (max !== undefined) clamped = Math.min(max, clamped);
    setText(formatDuration(clamped));
    if (clamped !== value) onCommit(clamped);
  };

  return (
    <InputMask
      mask={durationMask}
      maskPlaceholder="00:00"
      className={styles.trimInput}
      value={text}
      onClick={e => e.stopPropagation()}
      onChange={e => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setText(formatDuration(value));
          (e.target as HTMLInputElement).blur();
        }
      }}
      disabled={disabled}
      inputMode="numeric"
    />
  );
};

const isVideoUrl = (url: string) => {
  const lower = url.toLowerCase().split('?')[0];
  return (
    lower.endsWith('.mp4') ||
    lower.endsWith('.mov') ||
    lower.endsWith('.webm') ||
    lower.endsWith('.m4v')
  );
};

const renderThumb = (
  slot: Slot,
  status: SlotStatus,
  className: string,
): React.ReactNode => {
  if (status === 'empty')
    return <div className={`${className} ${styles.thumbEmpty}`} />;
  const ref = slot.current ?? slot.original;
  const url = ref?.thumbnailUrl;
  if (!ref || !url)
    return <div className={`${className} ${styles.thumbEmpty}`} />;
  const cls = `${className} ${status === 'deleted' ? styles.thumbDeleted : ''}`;
  if (isVideoUrl(url))
    return <video key={url} src={url} className={cls} muted />;
  return <img key={url} src={url} className={cls} alt="" />;
};

const BADGE_CLASS: Record<SlotBadge, string> = {
  new: 'badgeNew',
  replaced: 'badgeReplaced',
  edited: 'badgeEdited',
  deleted: 'badgeDeleted',
  reordered: 'badgeReordered',
};

const BADGE_TEXT_KEY: Record<SlotBadge, string> = {
  new: 'editMediaBadgeNew',
  replaced: 'editMediaBadgeReplaced',
  edited: 'editMediaBadgeEdited',
  deleted: 'editMediaBadgeDeleted',
  reordered: 'editMediaBadgeReordered',
};

interface SlotItemProps {
  slot: Slot;
  selected: boolean;
  showActions: boolean;
  status: SlotStatus;
  disabled: boolean;
  isReplaceAllMode: boolean;
  isReordered: boolean;
  freeDuration: number;
  chunkDuration: number;
  onSelect: (key: string) => void;
  onDelete: (key: string) => void;
  onRevert: (key: string) => void;
  onTrim: (key: string, startTime: number, endTime: number) => void;
}

const ClipSlotItem = ({
  slot,
  selected,
  showActions,
  status,
  disabled,
  isReplaceAllMode,
  isReordered,
  freeDuration,
  chunkDuration,
  onSelect,
  onDelete,
  onRevert,
  onTrim,
}: SlotItemProps) => {
  const {setNodeRef, attributes, listeners, transform, transition, isDragging} =
    useSortable({id: slot.slotKey, disabled: disabled || isSlotLocked(slot)});

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const ref = slot.current;
  const typeRef = ref ?? slot.original;
  const locked = isSlotLocked(slot);
  const canRevert = !locked && slot.original !== null && status !== 'unchanged';
  const showTrim = !locked && !!ref;
  const trimDisabled = disabled || !showActions;
  const effectiveShowActions = showActions && !locked;
  const badge = !locked ? slotBadgeOf(slot, isReordered) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.slotItem} ${locked ? styles.slotItemLocked : ''} ${
        selected ? styles.slotItemSelected : ''
      }`}
      title={
        isReplaceAllMode ? VideoEditorText.editMediaReplaceAllHint : undefined
      }
      onClick={() => !disabled && !locked && onSelect(slot.slotKey)}>
      {canRevert && (
        <button
          className={styles.slotRevertBtn}
          onClick={e => {
            e.stopPropagation();
            onRevert(slot.slotKey);
          }}
          disabled={disabled}
          title={VideoEditorText.editMediaRevert}
          aria-label={VideoEditorText.editMediaRevert}>
          ↺
        </button>
      )}
      <div className={styles.slotTopRow}>
        <span className={styles.slotTypeIcon}>
          {typeRef?.kind === 'image' ? <ImageTypeIcon /> : <VideoTypeIcon />}
        </span>
        <div className={styles.slotThumbBlock}>
          <div className={styles.slotThumbWrapper}>
            {renderThumb(slot, status, styles.slotThumb)}
          </div>
          {showTrim && ref!.kind === 'image' && (
            <div className={styles.trimRow}>
              <TimeInput
                value={Math.max(0, ref!.endTime - ref!.startTime)}
                disabled={trimDisabled}
                min={1}
                max={chunkDuration > 0 ? chunkDuration : undefined}
                onCommit={dur => onTrim(slot.slotKey, 0, Math.max(1, dur))}
              />
            </div>
          )}
          {showTrim && ref!.kind === 'video' && (
            <div className={styles.trimRow}>
              <TimeInput
                value={ref!.startTime}
                disabled={trimDisabled}
                min={0}
                max={Math.max(0, ref!.endTime - 1)}
                onCommit={start => onTrim(slot.slotKey, start, ref!.endTime)}
              />
              <span className={styles.trimDash}>-</span>
              <TimeInput
                value={ref!.endTime}
                disabled={trimDisabled}
                min={ref!.startTime + 1}
                max={ref!.fileDuration > 0 ? ref!.fileDuration : undefined}
                onCommit={end => onTrim(slot.slotKey, ref!.startTime, end)}
              />
              {ref!.fileDuration > 0 && (
                <span className={styles.fileDurationHint}>
                  / {formatDuration(ref!.fileDuration)}
                </span>
              )}
            </div>
          )}
          {locked && typeRef && (
            <div className={styles.trimRow}>
              <TimeInput
                value={slotDurationOf(slot)}
                disabled
                onCommit={() => undefined}
              />
            </div>
          )}
        </div>
        <div className={styles.slotMeta}>
          {status === 'empty' ? (
            <>
              <span className={styles.slotEmptyLabel}>
                {VideoEditorText.editMediaSelectMedia}
              </span>
              {freeDuration > 0 && (
                <span className={styles.slotDurationDim}>
                  {formatDuration(freeDuration)}
                </span>
              )}
            </>
          ) : badge ? (
            <span
              className={`${styles.slotBadge} ${styles[BADGE_CLASS[badge]]}`}>
              {VideoEditorText[BADGE_TEXT_KEY[badge]]}
            </span>
          ) : null}
        </div>
        {locked && (
          <span
            className={styles.slotLockIndicator}
            title={VideoEditorText.editMediaLocked}>
            <LockIcon />
          </span>
        )}
        {effectiveShowActions && (
          <div className={styles.slotActions}>
            {status !== 'deleted' && status !== 'empty' && (
              <button
                className={`${styles.slotActionBtn} ${styles.deleteAction}`}
                onClick={e => {
                  e.stopPropagation();
                  onDelete(slot.slotKey);
                }}
                disabled={disabled}
                title={VideoEditorText.editMediaDelete}>
                <DeleteIcon className={styles.deleteIcon} />
              </button>
            )}
            <button
              className={styles.slotGripBtn}
              onClick={e => e.stopPropagation()}
              disabled={disabled}
              title={VideoEditorText.editMediaReorder}
              aria-label={VideoEditorText.editMediaReorder}
              {...attributes}
              {...listeners}>
              ⠿
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface ClipPreviewProps {
  slot: Slot | null;
  status: SlotStatus | null;
}

const ClipPreview = ({slot, status}: ClipPreviewProps) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const ref = slot?.current ?? null;
  const startTime = ref?.startTime ?? 0;

  React.useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onLoaded = () => {
      el.currentTime = startTime;
    };
    el.addEventListener('loadedmetadata', onLoaded);
    if (el.readyState >= 1) onLoaded();
    return () => {
      el.removeEventListener('loadedmetadata', onLoaded);
    };
  }, [ref?.previewUrl, startTime]);

  if (!slot || status === 'empty' || status === 'deleted' || !ref) {
    return (
      <div className={styles.previewEmpty}>
        {VideoEditorText.editMediaPreviewEmpty}
      </div>
    );
  }
  if (ref.kind === 'image') {
    return (
      <img
        key={ref.previewUrl}
        src={ref.previewUrl}
        className={styles.previewMedia}
        alt=""
      />
    );
  }
  return (
    <video
      ref={videoRef}
      key={ref.previewUrl}
      src={ref.previewUrl}
      className={styles.previewMedia}
      controls
      playsInline
    />
  );
};

const PreviewTrimHint = ({slot}: {slot: Slot | null}) => {
  const ref = slot?.current ?? null;
  if (!ref || ref.kind !== 'video' || ref.fileDuration <= 0) return null;
  return (
    <div className={styles.previewTrimHint}>
      {VideoEditorText.editMediaTrimLabel}: {formatDuration(ref.startTime)} -{' '}
      {formatDuration(ref.endTime)} / {formatDuration(ref.fileDuration)}
    </div>
  );
};

interface Props {
  state: ReturnType<typeof useEditMediaState>;
  disabled?: boolean;
}

const EditMediaSlotsPanel = React.memo(({state, disabled = false}: Props) => {
  const replaceAllPreviewSlot: Slot | null =
    state.isReplaceAllMode && state.pending
      ? {
          slotKey: 'replace-all-preview',
          original: null,
          current: {
            artifactId: null,
            source: state.pending.source,
            libraryMediaFileId: state.pending.libraryMediaFileId,
            storyBlocksVideoId: state.pending.storyBlocksVideoId,
            thumbnailUrl: state.pending.thumbnailUrl,
            previewUrl: state.pending.previewUrl,
            fileDuration: state.pending.fileDuration,
            kind: state.pending.kind,
            startTime: 0,
            endTime: state.pending.fileDuration,
            isNotEditable: false,
          },
        }
      : null;

  const previewSlot = state.isReplaceAllMode
    ? replaceAllPreviewSlot
    : state.selectedSlot;
  const previewStatus = previewSlot ? state.slotStatusOf(previewSlot) : null;

  const hasMedia = state.slots.some(s => !!s.current);

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (!over || active.id === over.id) return;
    state.reorderSlots(String(active.id), String(over.id));
  };

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>
            {VideoEditorText.editMediaClipSlots}
          </span>
          {state.chunkDuration > 0 && (
            <span
              className={`${styles.sectionTotal} ${
                hasMedia && state.durationStatus === 'over'
                  ? styles.sectionTotalOver
                  : hasMedia && state.durationStatus === 'under'
                  ? styles.sectionTotalUnder
                  : ''
              }`}>
              {formatDuration(state.usedDuration)} /{' '}
              {formatDuration(state.chunkDuration)}
            </span>
          )}
        </div>
        {hasMedia &&
          state.chunkDuration > 0 &&
          state.durationStatus !== 'match' && (
            <div
              className={`${styles.durationWarning} ${
                state.durationStatus === 'over'
                  ? styles.warningOver
                  : styles.warningUnder
              }`}>
              {state.durationStatus === 'over'
                ? `${VideoEditorText.editMediaWarnOver} ${Math.abs(
                    state.durationDelta,
                  )}s`
                : `${VideoEditorText.editMediaWarnUnder} ${Math.abs(
                    state.durationDelta,
                  )}s`}
            </div>
          )}
        {!hasMedia && state.chunkDuration > 0 && (
          <div className={`${styles.durationWarning} ${styles.warningNeutral}`}>
            {state.slots.every(s => !s.original)
              ? VideoEditorText.editMediaEmptyChunkHint
              : VideoEditorText.editMediaNoMediaHint}
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}>
          <SortableContext
            items={state.slots.map(s => s.slotKey)}
            strategy={verticalListSortingStrategy}>
            <div className={styles.slotsList}>
              {state.slots.map((slot, idx) => {
                const originalIdx = state.originalSlots.findIndex(
                  o => o.slotKey === slot.slotKey,
                );
                const isReordered = originalIdx !== -1 && originalIdx !== idx;
                return (
                  <ClipSlotItem
                    key={slot.slotKey}
                    slot={slot}
                    status={state.slotStatusOf(slot)}
                    selected={
                      state.isReplaceAllMode ||
                      state.selectedSlotKey === slot.slotKey
                    }
                    showActions={
                      !state.isReplaceAllMode &&
                      state.selectedSlotKey === slot.slotKey
                    }
                    disabled={disabled || state.isReplaceAllMode}
                    isReplaceAllMode={state.isReplaceAllMode}
                    isReordered={isReordered}
                    freeDuration={state.freeDuration}
                    chunkDuration={state.chunkDuration}
                    onSelect={state.selectSlot}
                    onDelete={state.deleteSlot}
                    onRevert={state.revertSlot}
                    onTrim={state.setSlotTrim}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
        <button
          className={styles.addSlotButton}
          onClick={state.addEmptySlot}
          disabled={!state.canAddSlot || disabled || state.isReplaceAllMode}>
          <span>{VideoEditorText.editMediaAddSlot}</span>
          {state.freeDuration > 0 && (
            <span className={styles.addSlotCount}>
              {state.freeDuration} {VideoEditorText.editMediaSecondsLeft}
            </span>
          )}
        </button>
        <button
          className={`${styles.replaceAllButton} ${
            state.isReplaceAllMode ? styles.replaceAllButtonActive : ''
          }`}
          onClick={
            state.isReplaceAllMode
              ? state.exitReplaceAllMode
              : state.enterReplaceAllMode
          }
          disabled={disabled}>
          {state.isReplaceAllMode
            ? VideoEditorText.editMediaCancelReplaceAll
            : VideoEditorText.editMediaReplaceAll}
        </button>
      </div>

      <div className={styles.section}>
        <span className={styles.sectionLabel}>
          {VideoEditorText.editMediaClipPreview}
        </span>
        <div className={styles.previewWrapper}>
          <ClipPreview slot={previewSlot} status={previewStatus} />
        </div>
        <PreviewTrimHint slot={state.selectedSlot} />
      </div>
    </div>
  );
});

export default EditMediaSlotsPanel;
